/**
 * Indicadores (BI): Equipe, Processos e casos, Controladoria, Financeiro e
 * Metas. Filtros comuns (período, unidade, área, pessoa) e exportação para
 * Excel do que está na tela. O que cada um vê segue as permissões.
 */
import { RememberedFilterNotice } from "@/ui/filter-notice";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import { Link, useSearch, useTitle } from "@/lib/router";
import { addDaysIso, fmtDate, fmtMoney, todayIso } from "@/lib/format";
import { useLookups } from "@/lib/catalog";
import {
  AreaSelect, Badge, Card, Combobox, ErrorBox, Field, FormGrid, Loading, Modal, PageHeader, Stat, Tabs, UnitSelect, UserSelect, useAct, CopyButton,
} from "@/ui/kit";
import { BarChart, GoalBar, HBars, monthLabel } from "@/ui/charts";
import { confirmar, toast } from "@/ui/feedback";
import { useUnitStore } from "@/lib/unit";
import { SocioView } from "./SocioView";
import { FinancePanel } from "./FinancePanel";
import { CaptacaoCard } from "@/ui/captacao";
import { ScoreRanking } from "./SettingsScoring";

type Tab = "socio" | "equipe" | "processos" | "controladoria" | "financeiro" | "metas" | "pontuacao";
type Cell = string | number | null;
interface Sheet { name: string; columns: string[]; rows: Cell[][] }

const PRESETS: Array<{ key: string; label: string }> = [
  { key: "30", label: "Últimos 30 dias" }, { key: "90", label: "Últimos 90 dias" }, { key: "180", label: "Últimos 6 meses" },
  { key: "365", label: "Últimos 12 meses" }, { key: "mes", label: "Este mês" }, { key: "ano", label: "Este ano" }, { key: "custom", label: "Personalizado" },
];
const COLORS = { brand: "var(--primary)", red: "var(--red, #c0392b)", green: "var(--green, #2e8b57)", amber: "var(--amber, #d99a00)", gray: "#9aa0a6", blue: "#4a7fd6" };

function useFilters() {
  const [search, set] = useSearch();
  const unit = useUnitStore((s) => s.unitId);
  const preset = search.get("periodo") ?? "90";
  const today = todayIso();
  let from = search.get("de") ?? "", to = search.get("ate") ?? "";
  if (preset !== "custom") {
    to = today;
    from = preset === "mes" ? `${today.slice(0, 8)}01` : preset === "ano" ? `${today.slice(0, 5)}01-01` : addDaysIso(today, -(Number(preset) - 1));
  }
  const f = { from, to, unitId: search.get("unidade") ?? unit, areaId: search.get("area") ?? "", userId: search.get("pessoa") ?? "" };
  return { f, preset, set, query: qs({ from: f.from, to: f.to, unitId: f.unitId, areaId: f.areaId, userId: f.userId }) };
}

async function exportXlsx(title: string, sheets: Sheet[]): Promise<void> {
  try {
    await api.downloadPost("/bi/exportar", { title, sheets }, `${title}.xlsx`);
  } catch (e) {
    toast.error((e as Error).message);
  }
}

export function IndicatorsPage(): JSX.Element {
  useTitle("Indicadores");
  const { can } = useLookups();
  const { f, preset, set } = useFilters();
  const [search] = useSearch();
  const socio = can("bi.socio");
  const asked = search.get("aba") as Tab | null;
  const tab: Tab = asked && !(asked === "socio" && !socio) ? asked : socio ? "socio" : "equipe";
  return (
    <div className="stack gap-16">
      <PageHeader title="Indicadores" subtitle="Visão do sócio, equipe, processos, controladoria, financeiro e metas" />
      <RememberedFilterNotice screen="indicadores" keys={["periodo", "de", "ate", "unidade", "area", "pessoa"]} dateKeys={["de", "ate"]}
        labels={(lk) => ({ periodo: (v) => ["Período", v === "custom" ? "personalizado" : v === "mes" ? "mês atual" : v === "ano" ? "ano atual" : `últimos ${v} dias`], unidade: (v) => ["Unidade", lk.unitName(v)], area: (v) => ["Área", lk.areaName(v)], pessoa: (v) => ["Pessoa", lk.userName(v)] })} />
      <Tabs<Tab> value={tab} onChange={(t) => set({ aba: t })} tabs={[
        { key: "socio", label: "Visão do sócio", hidden: !socio },
        { key: "equipe", label: "Equipe" },
        { key: "processos", label: "Processos e casos", hidden: !can("processo.ver") },
        { key: "controladoria", label: "Controladoria", hidden: !can("comunicacao.ver") },
        { key: "financeiro", label: "Financeiro", hidden: !can("financeiro.gerente") },
        { key: "metas", label: "Metas" },
        { key: "pontuacao", label: "Pontuação" },
      ]} />
      {tab !== "metas" && tab !== "socio" && (
        <Card>
          <div className="filters-bar">
            <Field label="Período">
              <select className="input" value={preset} onChange={(e) => set({ periodo: e.target.value, de: e.target.value === "custom" ? f.from : null, ate: e.target.value === "custom" ? f.to : null })}>
                {PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </Field>
            {preset === "custom" && <>
              <Field label="De"><input type="date" className="input" value={f.from} onChange={(e) => set({ de: e.target.value })} /></Field>
              <Field label="Até"><input type="date" className="input" value={f.to} onChange={(e) => set({ ate: e.target.value })} /></Field>
            </>}
            <Field label="Unidade"><UnitSelect value={f.unitId || null} onChange={(v) => { useUnitStore.getState().set(v ?? ""); set({ unidade: null }); }} allowEmpty /></Field>
            <Field label="Área"><AreaSelect value={f.areaId || null} onChange={(v) => set({ area: v })} allowEmpty /></Field>
            {(tab === "equipe" || tab === "processos") && <Field label="Pessoa"><UserSelect value={f.userId || null} onChange={(v) => set({ pessoa: v })} emptyLabel="Todas" /></Field>}
          </div>
        </Card>
      )}
      {tab === "socio" && <SocioView embedded />}
      {tab === "equipe" && <Team />}
      {tab === "processos" && <Processes />}
      {tab === "controladoria" && <Control />}
      {tab === "financeiro" && <Finance />}
      {tab === "metas" && <Goals />}
      {tab === "pontuacao" && (
        <Card title="Ranking de pontuação" actions={<span className="muted small">Pontos de quem concluiu · fora do prazo interno vale menos (Configurações › Pontuação)</span>}>
          <ScoreRanking from={f.from} to={f.to} unitId={f.unitId || null} />
        </Card>
      )}
    </div>
  );
}

const pct = (v: number | null | undefined) => (v === null || v === undefined ? "—" : `${v}%`);
const ExportBtn = ({ onClick }: { onClick: () => void }) => <button className="btn small" onClick={onClick}>Exportar Excel</button>;

// ================================================================= equipe

interface TeamData {
  scope: "escritorio" | "unidade" | "proprio"; from: string; to: string; days: string[]; future: string[];
  totals: { done: number; open: number; overdue: number; fatalMissed: number; onTimePct: number | null };
  users: Array<{ id: string; name: string; done: number; onTimePct: number | null; fatalMissed: number; avgDays: number | null; returns: number; open: number; overdue: number; fatalOverdue: number; heat: Record<string, number>; planned: Record<string, number> }>;
  weekly: Array<{ week: string; done: number; onTimePct: number | null }>;
}
const SCOPE_LABEL = { escritorio: "todo o escritório", unidade: "sua(s) unidade(s)", proprio: "apenas os seus números" };

function Team(): JSX.Element {
  const { query } = useFilters();
  const { data, error } = useQuery({ queryKey: ["bi-equipe", query], queryFn: () => api.get<TeamData>(`/bi/equipe${query}`) });
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading lines={6} />;
  const max = Math.max(1, ...data.users.flatMap((u) => [...Object.values(u.heat), ...Object.values(u.planned)]));
  const heat = (n: number) => (!n ? "h0" : n / max < 0.34 ? "h1" : n / max < 0.67 ? "h2" : "h3");
  const doExport = () => void exportXlsx("Indicadores - Equipe", [
    { name: "Pessoas", columns: ["Pessoa", "Concluídas", "% no prazo", "Tempo médio (dias)", "Devoluções", "Abertas", "Atrasadas", "Fatais perdidos", "Fatais vencidos em aberto"],
      rows: data.users.map((u) => [u.name, u.done, u.onTimePct, u.avgDays, u.returns, u.open, u.overdue, u.fatalMissed, u.fatalOverdue]) },
    { name: "Semanas", columns: ["Semana (segunda)", "Concluídas", "% no prazo"], rows: data.weekly.map((w) => [w.week, w.done, w.onTimePct]) },
    { name: "Concluídas por dia", columns: ["Pessoa", ...data.days], rows: data.users.map((u) => [u.name, ...data.days.map((d) => u.heat[d] ?? 0)]) },
  ]);
  return (
    <div className="stack gap-16">
      <div className="muted small">Você está vendo: <b>{SCOPE_LABEL[data.scope]}</b>. Período {fmtDate(data.from)} a {fmtDate(data.to)}.</div>
      <div className="grid cols-4">
        <Stat label="Atividades concluídas" value={data.totals.done} />
        <Stat label="Entregues no prazo interno" value={pct(data.totals.onTimePct)} tone={data.totals.onTimePct !== null && data.totals.onTimePct < 80 ? "warn" : "ok"} />
        <Stat label="Atrasadas agora" value={data.totals.overdue} tone={data.totals.overdue ? "bad" : "ok"} hint={`${data.totals.open} abertas`} />
        <Stat label="Prazos fatais perdidos" value={data.totals.fatalMissed} tone={data.totals.fatalMissed ? "bad" : "ok"} />
      </div>
      <Card title="Entregas por semana" actions={<ExportBtn onClick={doExport} />}>
        <BarChart data={data.weekly} x="week" labelX={(w) => `${w.slice(8, 10)}/${w.slice(5, 7)}`}
          series={[{ key: "done", label: "Concluídas", color: COLORS.brand }, { key: "onTimePct", label: "% no prazo", color: COLORS.green, kind: "line" }]} />
      </Card>
      <Card title="Por pessoa">
        <div className="table-wrap">
          <table className="table bi-table">
            <thead><tr><th>Pessoa</th><th>Concluídas</th><th>% no prazo</th><th>Tempo médio</th><th>Devoluções</th><th>Abertas</th><th>Atrasadas</th><th>Fatais perdidos</th></tr></thead>
            <tbody>
              {data.users.map((u) => (
                <tr key={u.id}>
                  <td><Link to={`/atividades?resp=${u.id}`}>{u.name}</Link></td>
                  <td>{u.done}</td>
                  <td className={u.onTimePct !== null && u.onTimePct < 80 ? "text-bad" : ""}>{pct(u.onTimePct)}</td>
                  <td>{u.avgDays === null ? "—" : `${u.avgDays} d`}</td>
                  <td>{u.returns || "—"}</td>
                  <td>{u.open}</td>
                  <td className={u.overdue ? "text-bad" : ""}>{u.overdue}</td>
                  <td className={u.fatalMissed ? "text-bad" : ""}>{u.fatalMissed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <Card title="Mapa de calor — concluídas por dia e carga planejada">
        <div className="heat-wrap">
          <table className="heat">
            <thead><tr><th />
              {data.days.map((d) => <th key={d}>{d.slice(8, 10)}<br />{d.slice(5, 7)}</th>)}
              <th style={{ minWidth: 12 }} />
              {data.future.map((d) => <th key={`f${d}`} style={{ color: "var(--primary)" }}>{d.slice(8, 10)}<br />{d.slice(5, 7)}</th>)}
            </tr></thead>
            <tbody>
              {data.users.map((u) => (
                <tr key={u.id}>
                  <td className="who"><b>{u.name}</b></td>
                  {data.days.map((d) => { const n = u.heat[d] ?? 0; return <td key={d} className={`cell hist ${heat(n)}`} title={`${u.name} · ${d}: ${n} concluída(s)`} style={{ cursor: "default" }}>{n || ""}</td>; })}
                  <td />
                  {data.future.map((d) => { const n = u.planned[d] ?? 0; return <td key={`f${d}`} className={`cell ${heat(n)}`} title={`${u.name} · ${d}: ${n} planejada(s)`} style={{ cursor: "default", boxShadow: "inset 0 0 0 1px var(--primary)" }}>{n || ""}</td>; })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="muted small">À esquerda, atividades concluídas em cada dia útil do período (até 92 dias). À direita (borda azul), carga já planejada para os próximos 10 dias úteis.</div>
      </Card>
    </div>
  );
}

// ============================================================== processos

interface StageRow { id: string; name: string; color: string; isFinal: boolean; count: number; avgDays: number | null }
interface ProcData {
  totals: { processosAtivos: number; processos: number; casosAtivos: number; semRevisao30: number };
  processStages: StageRow[]; caseStages: StageRow[];
  monthly: Array<{ month: string; processosNovos: number; casosNovos: number; processosEncerrados: number; casosEncerrados: number }>;
  byArea: Array<{ name: string; count: number }>; byActionType: Array<{ name: string; count: number }>;
  stale: Array<{ id: string; cnjNumber: string | null; title: string | null; lastReviewedAt: string | null; responsibleUserId: string }>;
}

function Processes(): JSX.Element {
  const { query } = useFilters();
  const { userName } = useLookups();
  const { data, error } = useQuery({ queryKey: ["bi-processos", query], queryFn: () => api.get<ProcData>(`/bi/processos${query}`) });
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading lines={6} />;
  const stageRows = (xs: StageRow[]) => xs.map((s) => ({ name: s.name, value: s.count, color: s.color, hint: s.avgDays !== null && s.count ? `· ${s.avgDays} d na etapa` : undefined }));
  const doExport = () => void exportXlsx("Indicadores - Processos e casos", [
    { name: "Etapas processos", columns: ["Etapa", "Quantidade", "Dias médios na etapa atual"], rows: data.processStages.map((s) => [s.name, s.count, s.avgDays]) },
    { name: "Etapas casos", columns: ["Etapa", "Quantidade", "Dias médios na etapa atual"], rows: data.caseStages.map((s) => [s.name, s.count, s.avgDays]) },
    { name: "Mensal", columns: ["Mês", "Processos novos", "Processos encerrados", "Casos novos", "Casos encerrados"], rows: data.monthly.map((m) => [m.month, m.processosNovos, m.processosEncerrados, m.casosNovos, m.casosEncerrados]) },
    { name: "Por área", columns: ["Área", "Processos ativos"], rows: data.byArea.map((a) => [a.name, a.count]) },
    { name: "Por tipo de ação", columns: ["Tipo de ação", "Processos ativos"], rows: data.byActionType.map((a) => [a.name, a.count]) },
    { name: "Sem revisão", columns: ["Processo", "Título", "Última revisão", "Responsável"], rows: data.stale.map((p) => [p.cnjNumber, p.title, p.lastReviewedAt?.slice(0, 10) ?? "nunca", userName(p.responsibleUserId)]) },
  ]);
  return (
    <div className="stack gap-16">
      <div className="grid cols-4">
        <Stat label="Processos ativos" value={data.totals.processosAtivos} hint={`${data.totals.processos} no total`} />
        <Stat label="Casos ativos" value={data.totals.casosAtivos} />
        <Stat label="Sem revisão há 30+ dias" value={data.totals.semRevisao30} tone={data.totals.semRevisao30 ? "warn" : "ok"} />
        <Stat label="Novos no último mês" value={data.monthly[data.monthly.length - 1]?.processosNovos ?? 0} hint="processos" />
      </div>
      <div className="grid cols-2">
        <Card title="Processos por etapa" actions={<ExportBtn onClick={doExport} />}><HBars rows={stageRows(data.processStages)} /></Card>
        <Card title="Casos por etapa"><HBars rows={stageRows(data.caseStages)} /></Card>
      </div>
      <Card title="Entradas × encerramentos (12 meses)">
        <BarChart data={data.monthly} x="month" labelX={monthLabel} series={[
          { key: "processosNovos", label: "Processos novos", color: COLORS.brand },
          { key: "processosEncerrados", label: "Processos encerrados", color: COLORS.gray },
          { key: "casosNovos", label: "Casos novos", color: COLORS.blue },
          { key: "casosEncerrados", label: "Casos encerrados", color: COLORS.amber },
        ]} />
        <div className="muted small">Encerrado = chegou a uma etapa final do quadro no mês.</div>
      </Card>
      <div className="grid cols-2">
        <Card title="Processos ativos por área"><HBars rows={data.byArea.map((a) => ({ name: a.name, value: a.count }))} /></Card>
        <Card title="Por tipo de ação (top 15)"><HBars rows={data.byActionType.map((a) => ({ name: a.name, value: a.count }))} /></Card>
      </div>
      <Card title="Sem revisão há mais de 30 dias">
        {data.stale.length ? data.stale.map((p) => (
          <div key={p.id} className="lrow"><span className="grow"><Link to={`/processos/${p.id}`}><span className="mono">{p.cnjNumber ?? p.title}</span></Link>{p.cnjNumber && <CopyButton text={p.cnjNumber} title="Copiar número do processo" />}</span>
            <span className="muted small">{userName(p.responsibleUserId)}</span><Badge tone="warn">{p.lastReviewedAt ? fmtDate(p.lastReviewedAt) : "nunca revisado"}</Badge></div>
        )) : <div className="muted small">Todos revisados nos últimos 30 dias.</div>}
      </Card>
    </div>
  );
}

// ========================================================== controladoria

interface CtlData {
  totals: { recebidas: number; analisadas: number; pendentesAgora: number; pct48h: number | null; prazos: number; cumpridos: number; perdidos: number; pendentes: number; semAtividade: number; divergentes: number };
  monthly: Array<{ month: string; recebidas: number; analisadas: number; pct48h: number | null; horasMedias: number | null }>;
  byTribunal: Array<{ name: string; count: number }>; byStatus: Array<{ name: string; count: number }>;
}
const STATUS_NAME: Record<string, string> = { nao_analisada: "Não analisada", conferir: "Aviso do tribunal", ciencia: "Ciência", com_obrigacao: "Com prazo/providência", descartada: "Descartada" };

function Control(): JSX.Element {
  const { query } = useFilters();
  const { data, error } = useQuery({ queryKey: ["bi-controladoria", query], queryFn: () => api.get<CtlData>(`/bi/controladoria${query}`) });
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading lines={6} />;
  const t = data.totals;
  const doExport = () => void exportXlsx("Indicadores - Controladoria", [
    { name: "Mensal", columns: ["Mês", "Recebidas", "Analisadas", "% em 48h", "Horas médias até análise"], rows: data.monthly.map((m) => [m.month, m.recebidas, m.analisadas, m.pct48h, m.horasMedias]) },
    { name: "Por tribunal", columns: ["Tribunal", "Intimações"], rows: data.byTribunal.map((x) => [x.name, x.count]) },
    { name: "Prazos", columns: ["Situação", "Quantidade"], rows: [["Cumpridos", t.cumpridos], ["Perdidos", t.perdidos], ["Em aberto", t.pendentes], ["Sem atividade", t.semAtividade], ["Divergentes na conferência", t.divergentes]] },
  ]);
  return (
    <div className="stack gap-16">
      <div className="grid cols-4">
        <Stat label="Intimações recebidas" value={t.recebidas} hint={`${t.pendentesAgora} aguardando análise agora`} />
        <Stat label="Analisadas em até 48h" value={pct(t.pct48h)} tone={t.pct48h !== null && t.pct48h < 90 ? "warn" : "ok"} />
        <Stat label="Prazos cumpridos" value={t.cumpridos} tone="ok" hint={`de ${t.prazos} com vencimento no período`} />
        <Stat label="Prazos perdidos" value={t.perdidos} tone={t.perdidos ? "bad" : "ok"} />
      </div>
      <Card title="Recebidas × analisadas (12 meses)" actions={<ExportBtn onClick={doExport} />}>
        <BarChart data={data.monthly} x="month" labelX={monthLabel} series={[
          { key: "recebidas", label: "Recebidas", color: COLORS.gray }, { key: "analisadas", label: "Analisadas", color: COLORS.brand },
          { key: "pct48h", label: "% em 48h", color: COLORS.green, kind: "line" },
        ]} />
      </Card>
      <div className="grid cols-2">
        <Card title="Por tribunal"><HBars rows={data.byTribunal.map((x) => ({ name: x.name, value: x.count }))} /></Card>
        <Card title="Prazos com vencimento no período">
          <HBars rows={[
            { name: "Cumpridos", value: t.cumpridos, color: COLORS.green }, { name: "Perdidos", value: t.perdidos, color: COLORS.red },
            { name: "Em aberto", value: t.pendentes, color: COLORS.blue }, { name: "Sem atividade vinculada", value: t.semAtividade, color: COLORS.gray },
            { name: "Divergentes na conferência", value: t.divergentes, color: COLORS.amber },
          ]} />
          <div className="muted small" style={{ marginTop: 8 }}>Resultado da análise no período: {data.byStatus.map((s) => `${STATUS_NAME[s.name] ?? s.name} ${s.count}`).join(" · ") || "—"}</div>
        </Card>
      </div>
    </div>
  );
}

// ============================================================= financeiro

interface FinData {
  monthly: Array<{ month: string; receita: number; deducoes: number; liquida: number; despesas: number; resultado: number; previsto: number }>;
  aging: Array<{ name: string; count: number; amount: number }>;
  totals: { receita: number; vencido: number };
  byArea: Array<{ name: string; amount: number }>; byActionType: Array<{ name: string; amount: number }>; byPartner: Array<{ name: string; amount: number }>;
  ticketByActionType: Array<{ name: string; count: number; avg: number }>;
}

function Finance(): JSX.Element {
  const { f } = useFilters();
  return (
    <div className="stack gap-16">
      <FinancePanel from={f.from} to={f.to} unitId={f.unitId} />
      <CaptacaoCard from={f.from} to={f.to} unitId={f.unitId} />
      <FinanceCharts />
    </div>
  );
}

function FinanceCharts(): JSX.Element {
  const { query } = useFilters();
  const { data, error } = useQuery({ queryKey: ["bi-financeiro", query], queryFn: () => api.get<FinData>(`/bi/financeiro${query}`) });
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading lines={6} />;
  const last = data.monthly[data.monthly.length - 1];
  const money = (v: number) => fmtMoney(v);
  const doExport = () => void exportXlsx("Indicadores - Financeiro", [
    { name: "DRE mensal", columns: ["Mês", "Previsto", "Receita", "Deduções parceiros", "Receita líquida", "Despesas", "Resultado"], rows: data.monthly.map((m) => [m.month, m.previsto, m.receita, m.deducoes, m.liquida, m.despesas, m.resultado]) },
    { name: "Inadimplência", columns: ["Faixa", "Parcelas", "Valor"], rows: data.aging.map((a) => [a.name, a.count, a.amount]) },
    { name: "Por área", columns: ["Área", "Receita"], rows: data.byArea.map((a) => [a.name, a.amount]) },
    { name: "Por tipo de ação", columns: ["Tipo de ação", "Receita"], rows: data.byActionType.map((a) => [a.name, a.amount]) },
    { name: "Por parceiro", columns: ["Parceiro", "Repasses"], rows: data.byPartner.map((a) => [a.name, a.amount]) },
    { name: "Ticket médio", columns: ["Tipo de ação", "Contratos", "Valor médio"], rows: data.ticketByActionType.map((a) => [a.name, a.count, a.avg]) },
  ]);
  return (
    <div className="stack gap-16">
      <div className="grid cols-4">
        <Stat label="Receita no período" value={money(data.totals.receita)} />
        <Stat label="Resultado do mês" value={money(last?.resultado ?? 0)} tone={(last?.resultado ?? 0) < 0 ? "bad" : "ok"} hint={last ? monthLabel(last.month) : ""} />
        <Stat label="Recebido × previsto no mês" value={last && last.previsto ? `${Math.round((last.receita / last.previsto) * 100)}%` : "—"} />
        <Stat label="A receber vencido" value={money(data.totals.vencido)} tone={data.totals.vencido ? "bad" : "ok"} />
      </div>
      <Card title="DRE gerencial — 12 meses" actions={<ExportBtn onClick={doExport} />}>
        <BarChart money data={data.monthly} x="month" labelX={monthLabel} series={[
          { key: "previsto", label: "Previsto", color: COLORS.gray }, { key: "receita", label: "Receita", color: COLORS.brand },
          { key: "despesas", label: "Despesas", color: COLORS.red }, { key: "resultado", label: "Resultado", color: COLORS.green },
        ]} />
      </Card>
      <div className="grid cols-2">
        <Card title="Inadimplência por atraso"><HBars rows={data.aging.map((a) => ({ name: a.name, value: a.amount, hint: `· ${a.count} parc.` }))} format={money} color={COLORS.red} /></Card>
        <Card title="Receita por área (período)"><HBars rows={data.byArea.map((a) => ({ name: a.name, value: a.amount }))} format={money} /></Card>
        <Card title="Receita por tipo de ação (período)"><HBars rows={data.byActionType.map((a) => ({ name: a.name, value: a.amount }))} format={money} /></Card>
        <Card title="Repasses a parceiros (período)"><HBars rows={data.byPartner.map((a) => ({ name: a.name, value: a.amount }))} format={money} color={COLORS.amber} /></Card>
      </div>
      <Card title="Valor médio contratado por tipo de ação (títulos criados no período)">
        <HBars rows={data.ticketByActionType.map((a) => ({ name: a.name, value: a.avg, hint: `· ${a.count} contrato(s)` }))} format={money} color={COLORS.blue} />
      </Card>
    </div>
  );
}

// ================================================================== metas

interface GoalRow {
  id: string; name: string; metric: string; scope: "escritorio" | "unidade" | "pessoa"; unitId: string | null; userId: string | null;
  period: "mes" | "trimestre"; startsOn: string; endsOn: string | null; target: number; active: boolean;
  value: number; projection: number; kind: "count" | "money" | "pct"; label: string; periodStart: string; periodEnd: string;
  elapsedPct: number; pct: number | null; onTrack: boolean | null;
}
interface GoalsData { ref: string; metrics: Array<{ key: string; label: string; kind: string }>; canManage: boolean; goals: GoalRow[] }

export const fmtGoal = (v: number, kind: string) => (kind === "money" ? fmtMoney(v) : kind === "pct" ? `${v}%` : String(Math.round(v * 10) / 10));

export function GoalCard({ g, compact }: { g: GoalRow; compact?: boolean }): JSX.Element {
  const { unitName, userName } = useLookups();
  const who = g.scope === "escritorio" ? "Escritório" : g.scope === "unidade" ? unitName(g.unitId) : userName(g.userId);
  return (
    <div className="goal-card">
      <div className="row between"><b>{g.name}</b>{!g.active && <Badge tone="muted">desativada</Badge>}</div>
      {!compact && <div className="muted small">{g.label} · {who} · {g.period === "mes" ? "mensal" : "trimestral"} ({fmtDate(g.periodStart)}–{fmtDate(g.periodEnd)})</div>}
      <div className="row between" style={{ alignItems: "baseline" }}>
        <span className="big">{fmtGoal(g.value, g.kind)}</span>
        <span className="muted small">meta {fmtGoal(g.target, g.kind)} · {g.pct ?? 0}%</span>
      </div>
      <GoalBar pct={g.pct} elapsedPct={g.elapsedPct} onTrack={g.onTrack} />
      <div className="small">
        {g.onTrack === null ? null : g.onTrack
          ? <span style={{ color: "var(--green, #2e8b57)" }}>No ritmo{g.kind !== "pct" ? `: projeção ${fmtGoal(g.projection, g.kind)}` : ""}</span>
          : <span className="text-bad">Abaixo do ritmo{g.kind !== "pct" ? `: projeção ${fmtGoal(g.projection, g.kind)}` : ""}</span>}
        <span className="muted"> · {g.elapsedPct}% do período</span>
      </div>
    </div>
  );
}

function Goals(): JSX.Element {
  const [ref, setRef] = useState(todayIso());
  const [all, setAll] = useState(false);
  const [creating, setCreating] = useState(false);
  const { data, error } = useQuery({ queryKey: ["bi-metas", ref, all], queryFn: () => api.get<GoalsData>(`/bi/metas${qs({ ref, todas: all ? "1" : undefined })}`) });
  const toggle = useAct((g: GoalRow) => api.patch(`/bi/metas/${g.id}`, { active: !g.active }), [["bi-metas"]]);
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading lines={4} />;
  return (
    <div className="stack gap-16">
      <Card>
        <div className="filters-bar">
          <Field label="Período de referência"><input type="date" className="input" value={ref} onChange={(e) => setRef(e.target.value || todayIso())} /></Field>
          {data.canManage && <label className="row nowrap small"><input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> mostrar desativadas e fora de vigência</label>}
          <span className="grow" />
          {data.canManage && <button className="btn primary" onClick={() => setCreating(true)}>Nova meta</button>}
        </div>
        <div className="muted small">A barra mostra o quanto da meta foi atingido; o traço, quanto do período já passou. "No ritmo" = a projeção até o fim do período alcança a meta.</div>
      </Card>
      {!data.goals.length ? <Card><div className="muted">Nenhuma meta {data.canManage ? "— crie a primeira em “Nova meta”." : "definida."}</div></Card> : (
        <div className="goal-grid">
          {data.goals.map((g) => (
            <div key={g.id} className="stack gap-4">
              <GoalCard g={g} />
              {data.canManage && <div className="row" style={{ justifyContent: "flex-end" }}>
                <button className="btn small ghost" onClick={() => void (g.active ? confirmar(`Desativar a meta "${g.name}"?`).then((ok) => ok && toggle.mutate(g)) : Promise.resolve(toggle.mutate(g)))}>{g.active ? "Desativar" : "Reativar"}</button>
              </div>}
            </div>
          ))}
        </div>
      )}
      {creating && <GoalModal metrics={data.metrics} onClose={() => setCreating(false)} />}
    </div>
  );
}

function GoalModal({ metrics, onClose }: { metrics: GoalsData["metrics"]; onClose: () => void }): JSX.Element {
  const [f, setF] = useState({ name: "", metric: "casos_novos", scope: "escritorio" as GoalRow["scope"], unitId: null as string | null, userId: null as string | null, period: "mes", startsOn: `${todayIso().slice(0, 8)}01`, endsOn: "", target: "" });
  const kind = metrics.find((m) => m.key === f.metric)?.kind;
  const save = useAct(() => api.post("/bi/metas", {
    ...f, name: f.name || metrics.find((m) => m.key === f.metric)?.label, endsOn: f.endsOn || null,
    target: kind === "money" ? Number(f.target.replace(/\./g, "").replace(",", ".")) : Number(f.target.replace(",", ".")),
  }), [["bi-metas"]], onClose, "Meta criada");
  return (
    <Modal title="Nova meta" onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancelar</button><button className="btn primary" disabled={!f.target || save.isPending} onClick={() => save.mutate()}>Criar</button></>}>
      <ErrorBox error={save.error} />
      <FormGrid cols={2}>
        <Field label="Indicador" span={2}><Combobox allowEmpty={false} value={f.metric} onChange={(v) => setF({ ...f, metric: v ?? "casos_novos" })} options={metrics.map((m) => ({ value: m.key, label: m.label }))} /></Field>
        <Field label="Nome (opcional)" span={2}><input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Ex.: Novos contratos da unidade Petrolina" /></Field>
        <Field label="De quem">
          <select className="input" value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value as GoalRow["scope"] })}>
            <option value="escritorio">Escritório todo</option><option value="unidade">Uma unidade</option><option value="pessoa">Uma pessoa</option>
          </select>
        </Field>
        {f.scope === "unidade" && <Field label="Unidade"><UnitSelect value={f.unitId} onChange={(v) => setF({ ...f, unitId: v })} /></Field>}
        {f.scope === "pessoa" && <Field label="Pessoa"><UserSelect value={f.userId} onChange={(v) => setF({ ...f, userId: v })} allowEmpty={false} /></Field>}
        {f.scope === "escritorio" && <div />}
        <Field label="Repete a cada">
          <select className="input" value={f.period} onChange={(e) => setF({ ...f, period: e.target.value })}><option value="mes">Mês</option><option value="trimestre">Trimestre</option></select>
        </Field>
        <Field label={kind === "money" ? "Meta (R$)" : kind === "pct" ? "Meta (%)" : "Meta (quantidade)"} required>
          <input className="input" inputMode="decimal" value={f.target} onChange={(e) => setF({ ...f, target: e.target.value })} />
        </Field>
        <Field label="Vale a partir de"><input type="date" className="input" value={f.startsOn} onChange={(e) => setF({ ...f, startsOn: e.target.value })} /></Field>
        <Field label="Até (opcional)"><input type="date" className="input" value={f.endsOn} onChange={(e) => setF({ ...f, endsOn: e.target.value })} /></Field>
      </FormGrid>
    </Modal>
  );
}
